import React, { createContext, useContext, useEffect, useState } from "react";
import webApi from "./webApi";

const WebAuthContext = createContext(null);

export function WebAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    // Always hit /me — the persistent session lives in an httpOnly cookie
    // that JS can't read. localStorage token is a backward-compat fallback.
    //
    // Hard 6s timeout: in webviews (Telegram, WhatsApp) the fetch can hang
    // indefinitely. We don't want the loading screen to be permanent.
    const guarded = new Promise((resolve, reject) => {
      const tid = setTimeout(() => reject(new Error("/me timed out")), 6000);
      webApi.get("/me")
        .then((r) => { clearTimeout(tid); resolve(r); })
        .catch((e) => { clearTimeout(tid); reject(e); });
    });
    try {
      const r = await guarded;
      setUser(r.data);
    } catch (e) {
      try {
        localStorage.removeItem("web_token");
      } catch (storageErr) {
        // Private-mode browsers can throw on localStorage write — log & continue
        console.debug("[webAuth] localStorage.removeItem failed:", storageErr?.message);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // ----- Primary: email/password -----
  const register = async (payload) => {
    const r = await webApi.post("/register", payload);
    localStorage.setItem("web_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const loginEmail = async (email, password, device_fingerprint) => {
    const r = await webApi.post("/login", { identifier: email, email, password, device_fingerprint });
    localStorage.setItem("web_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  // New canonical login: identifier may be email OR phone.
  const loginIdentifier = async (identifier, password, device_fingerprint, remember = true) => {
    const r = await webApi.post("/login", { identifier, password, device_fingerprint, remember });
    if (remember) {
      try {
        localStorage.setItem("web_token", r.data.token);
      } catch (storageErr) {
        console.debug("[webAuth] remember setItem failed:", storageErr?.message);
      }
    } else {
      try {
        localStorage.removeItem("web_token");
      } catch (storageErr) {
        console.debug("[webAuth] remember removeItem failed:", storageErr?.message);
      }
    }
    setUser(r.data.user);
    return r.data.user;
  };

  // ----- Legacy fallbacks (kept for existing Telegram users) -----
  const loginWithTelegram = async (tgPayload) => {
    const r = await webApi.post("/auth/telegram", tgPayload);
    localStorage.setItem("web_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const loginWithCode = async (code) => {
    const r = await webApi.post("/auth/code", { code });
    localStorage.setItem("web_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try {
      // Best-effort: tell the backend to clear the httpOnly session cookie
      await webApi.post("/logout");
    } catch (e) {
      // Surface in DevTools so it's debuggable, but never block the client-side cleanup.
      console.debug("[webAuth] /logout failed (clearing local state anyway):", e?.message);
    }
    localStorage.removeItem("web_token");
    setUser(null);
    window.location.href = "/";
  };

  const refreshCredits = async () => {
    try {
      const r = await webApi.get("/me");
      setUser(r.data);
    } catch (e) {
      console.error("[webAuth] error:", e);
    }
  };

  return (
    <WebAuthContext.Provider
      value={{
        user,
        loading,
        register,
        loginEmail,
        loginIdentifier,
        loginWithTelegram,
        loginWithCode,
        logout,
        refreshCredits,
      }}
    >
      {children}
    </WebAuthContext.Provider>
  );
}

export const useWebAuth = () => useContext(WebAuthContext);
